/**
 * Local experiment decision / learning log.
 * Feeds the Learn → Ideate loop until a server-side log lands.
 */

export type ExperimentLogEntry = {
  experimentId: string
  propertyId: string
  experimentName: string
  testGoal?: string
  testType?: string
  pageUrl?: string
  winnerVariantId: string | null
  winnerLabel?: string | null
  action: "ship" | "stop" | "no_ship"
  liftPercent?: number | null
  significancePercent?: number | null
  rationale?: string
  hypothesis?: string
  decidedAt: string
  domain?: string
}

const STORAGE_KEY = "humblytics:experiment:log:v1"

export function getExperimentLog(propertyId?: string): ExperimentLogEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const all = JSON.parse(raw) as ExperimentLogEntry[]
    if (!Array.isArray(all)) return []
    const sorted = [...all].sort(
      (a, b) => new Date(b.decidedAt).getTime() - new Date(a.decidedAt).getTime(),
    )
    if (!propertyId) return sorted
    return sorted.filter((e) => e.propertyId === propertyId)
  } catch {
    return []
  }
}

export function appendExperimentLog(entry: ExperimentLogEntry): void {
  try {
    const existing = getExperimentLog()
    const next = [entry, ...existing.filter((e) => e.experimentId !== entry.experimentId)]
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next.slice(0, 200)))
  } catch {
    /* ignore quota */
  }
}

function getExperimentDecision(experimentId: string): ExperimentLogEntry | null {
  return getExperimentLog().find((e) => e.experimentId === experimentId) ?? null
}

/**
 * Suggest a next page/pattern to test from a winning decision.
 */
export function nextBestActionFromLog(entry: ExperimentLogEntry): {
  title: string
  description: string
  ctaLabel: string
} | null {
  if (entry.action !== "ship" && entry.action !== "stop") return null
  if (!entry.winnerVariantId) return null
  const lift =
    entry.liftPercent != null && entry.liftPercent > 0
      ? `+${entry.liftPercent.toFixed(1)}%`
      : "a win"
  return {
    title: `This won ${lift}`,
    description: entry.pageUrl
      ? `Try the same pattern on a related high-traffic page. Start from ${entry.pageUrl}.`
      : "Try the same pattern on your next highest-traffic page.",
    ctaLabel: "Start next test",
  }
}
